import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { api } from "@/lib/api";

const ADMIN_KEY_STORAGE = "nirno_admin_key";

export default function AdminPanel() {
  const [key, setKey] = useState(() => localStorage.getItem(ADMIN_KEY_STORAGE) || "");
  const [authed, setAuthed] = useState(false);
  const [apps, setApps] = useState([]);
  const [accessReqs, setAccessReqs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [approvedLinks, setApprovedLinks] = useState({}); // app_id → invite token
  const [approvedCodes, setApprovedCodes] = useState({}); // request_id → secret code

  const load = async (k) => {
    setLoading(true);
    try {
      const [appsRes, accessRes] = await Promise.all([
        api.get("/admin/applications", { headers: { "X-Admin-Key": k } }),
        api.get("/admin/access-requests", { headers: { "X-Admin-Key": k } }),
      ]);
      setApps(appsRes.data);
      setAccessReqs(accessRes.data);
      setAuthed(true);
      localStorage.setItem(ADMIN_KEY_STORAGE, k);
    } catch (err) {
      setAuthed(false);
      toast.error(err.response?.data?.detail || "Invalid admin key");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (key) load(key); // eslint-disable-line
  }, []); // eslint-disable-line

  const handleApprove = async (id) => {
    try {
      const r = await api.post(`/admin/applications/${id}/approve`, null, { headers: { "X-Admin-Key": key } });
      setApprovedLinks((prev) => ({ ...prev, [id]: r.data.token }));
      toast.success("Invite link generated.");
      await load(key);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not approve");
    }
  };

  const handleApproveAccess = async (id) => {
    try {
      const r = await api.post(`/admin/access-requests/${id}/approve`, null, { headers: { "X-Admin-Key": key } });
      setApprovedCodes((prev) => ({ ...prev, [id]: r.data.code }));
      toast.success(r.data.email_sent ? "Access code emailed." : "Access code generated (email not sent — copy it manually).");
      await load(key);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not approve");
    }
  };

  const inviteUrl = (token) => `${window.location.origin}/invite/${token}`;
  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard.");
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[var(--paper)] flex items-center justify-center px-6" data-testid="admin-gate">
        <div className="w-full max-w-[400px]">
          <Logo size={28} />
          <span className="eyebrow mt-8 block">◆ Admin only</span>
          <h2 className="h-display text-[32px] mt-2">Enter admin key</h2>
          <input
            className="inp mt-5"
            type="password"
            placeholder="Admin key"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            data-testid="admin-key-input"
            onKeyDown={(e) => e.key === "Enter" && load(key)}
          />
          <button
            onClick={() => load(key)}
            data-testid="admin-key-submit"
            disabled={loading || !key}
            className="btn btn-solid full mt-3"
          >
            {loading ? "…" : "Continue →"}
          </button>
          <Link to="/" className="block text-center mt-6 text-[12px] text-[var(--ink3)] hover:text-[var(--ink)]">
            ← Back to site
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--paper)]" data-testid="admin-panel">
      <header className="border-b border-[var(--line)] bg-[var(--paper)]/85 backdrop-blur-md px-6 sm:px-10 py-5 flex items-center justify-between">
        <Logo />
        <div className="mono text-[10px] tracking-[0.2em] uppercase text-[var(--red)]">◆ Admin · Nirno HQ</div>
        <button
          onClick={() => { localStorage.removeItem(ADMIN_KEY_STORAGE); setAuthed(false); setKey(""); }}
          data-testid="admin-logout"
          className="btn btn-ghost btn-sm"
        >Log out</button>
      </header>

      <section className="max-w-[1280px] mx-auto px-6 sm:px-10 py-12">
        <span className="eyebrow">◆ Seller applications</span>
        <h1 className="h-display text-[40px] sm:text-[52px] mt-3">{apps.length} {apps.length === 1 ? "application" : "applications"}.</h1>

        {apps.length === 0 ? (
          <div className="mt-10 bg-[var(--card)] border-[1.5px] border-dashed border-[var(--line)] rounded-[18px] py-14 text-center text-[14px] text-[var(--ink3)]">
            No applications yet.
          </div>
        ) : (
          <div className="mt-8 bg-[var(--card)] border-[1.5px] border-[var(--line)] rounded-[18px] overflow-hidden divide-y divide-[var(--line)]" data-testid="apps-table">
            <div className="hidden md:grid grid-cols-[1.4fr_1.6fr_1fr_1fr_1fr_auto] gap-4 px-6 py-3 mono text-[10px] tracking-[0.15em] uppercase text-[var(--ink3)]">
              <span>Name</span><span>Email</span><span>Social</span><span>Submitted</span><span>Status</span><span>Action</span>
            </div>
            {apps.map((a) => (
              <div key={a.application_id} data-testid={`app-row-${a.application_id}`} className="px-6 py-5 grid md:grid-cols-[1.4fr_1.6fr_1fr_1fr_1fr_auto] gap-4 items-start md:items-center">
                <div className="font-extrabold text-[14px]">{a.name}</div>
                <div className="text-[13px] text-[var(--ink2)] break-all">{a.email}</div>
                <div className="text-[12px] mono text-[var(--ink2)]">{a.social_handle || "—"}</div>
                <div className="text-[12px] mono text-[var(--ink3)]">{new Date(a.created_at).toLocaleDateString()}</div>
                <div>
                  <span className={`mono text-[10px] tracking-[0.15em] uppercase px-2.5 py-1 rounded-full font-bold ${a.status === "approved" ? "bg-[var(--green)] text-white" : "bg-[var(--line)] text-[var(--ink2)]"}`}>
                    {a.status}
                  </span>
                </div>
                <div className="flex items-center gap-2 justify-end">
                  {a.status !== "approved" ? (
                    <button onClick={() => handleApprove(a.application_id)} data-testid={`approve-${a.application_id}`} className="btn btn-solid btn-sm">Approve</button>
                  ) : (
                    <button
                      onClick={() => copy(inviteUrl(a.invite_token || approvedLinks[a.application_id]))}
                      data-testid={`copy-${a.application_id}`}
                      className="btn btn-paper btn-sm"
                    >Copy link</button>
                  )}
                </div>

                {/* Application body & invite URL row spanning all columns */}
                <div className="md:col-span-6 mt-2 grid lg:grid-cols-2 gap-4">
                  <div className="bg-[var(--paper)] border border-[var(--line)] rounded-[14px] p-3 text-[13px] leading-[1.55]">
                    <div className="mono text-[10px] tracking-[0.15em] uppercase text-[var(--ink3)] mb-1">Destinations</div>
                    <div className="text-[var(--ink2)]">{a.destinations}</div>
                    <div className="mono text-[10px] tracking-[0.15em] uppercase text-[var(--ink3)] mt-3 mb-1">Motivation</div>
                    <div className="text-[var(--ink2)]">{a.motivation}</div>
                  </div>
                  {(a.invite_token || approvedLinks[a.application_id]) && (
                    <div className="bg-[var(--paper)] border border-[var(--red)]/40 rounded-[14px] p-3 text-[13px]">
                      <div className="mono text-[10px] tracking-[0.15em] uppercase text-[var(--red)] mb-1">Invite link · 7-day expiry</div>
                      <code className="block break-all text-[var(--ink)] mono text-[12px] mt-1">{inviteUrl(a.invite_token || approvedLinks[a.application_id])}</code>
                      {a.invite_used && <div className="mono text-[10px] tracking-[0.15em] uppercase text-[var(--green)] mt-2">◆ Redeemed</div>}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="max-w-[1280px] mx-auto px-6 sm:px-10 pb-16">
        <span className="eyebrow">◆ Access requests</span>
        <h1 className="h-display text-[40px] sm:text-[52px] mt-3">{accessReqs.length} {accessReqs.length === 1 ? "request" : "requests"}.</h1>

        {accessReqs.length === 0 ? (
          <div className="mt-10 bg-[var(--card)] border-[1.5px] border-dashed border-[var(--line)] rounded-[18px] py-14 text-center text-[14px] text-[var(--ink3)]">
            No access requests yet.
          </div>
        ) : (
          <div className="mt-8 bg-[var(--card)] border-[1.5px] border-[var(--line)] rounded-[18px] overflow-hidden divide-y divide-[var(--line)]" data-testid="access-requests-table">
            <div className="hidden md:grid grid-cols-[1.4fr_1.6fr_1fr_1fr_auto] gap-4 px-6 py-3 mono text-[10px] tracking-[0.15em] uppercase text-[var(--ink3)]">
              <span>Name</span><span>Email</span><span>Submitted</span><span>Status</span><span>Action</span>
            </div>
            {accessReqs.map((r) => (
              <div key={r.id} data-testid={`access-req-row-${r.id}`} className="px-6 py-5 grid md:grid-cols-[1.4fr_1.6fr_1fr_1fr_auto] gap-4 items-start md:items-center">
                <div className="font-extrabold text-[14px]">{r.name}</div>
                <div className="text-[13px] text-[var(--ink2)] break-all">{r.email}</div>
                <div className="text-[12px] mono text-[var(--ink3)]">{new Date(r.created_at).toLocaleDateString()}</div>
                <div>
                  <span className={`mono text-[10px] tracking-[0.15em] uppercase px-2.5 py-1 rounded-full font-bold ${r.status === "approved" ? "bg-[var(--green)] text-white" : "bg-[var(--line)] text-[var(--ink2)]"}`}>
                    {r.status}
                  </span>
                </div>
                <div className="flex items-center gap-2 justify-end">
                  {r.status !== "approved" ? (
                    <button onClick={() => handleApproveAccess(r.id)} data-testid={`approve-access-${r.id}`} className="btn btn-solid btn-sm">Approve</button>
                  ) : (
                    <button
                      onClick={() => copy(r.secret_code || approvedCodes[r.id])}
                      data-testid={`copy-code-${r.id}`}
                      className="btn btn-paper btn-sm"
                    >Copy code</button>
                  )}
                </div>

                {(r.secret_code || approvedCodes[r.id]) && (
                  <div className="md:col-span-5 mt-2">
                    <div className="bg-[var(--paper)] border border-[var(--red)]/40 rounded-[14px] p-3 text-[13px] inline-block">
                      <div className="mono text-[10px] tracking-[0.15em] uppercase text-[var(--red)] mb-1">Access code · never expires</div>
                      <code className="block text-[var(--ink)] mono text-[16px] tracking-[0.2em] mt-1">{r.secret_code || approvedCodes[r.id]}</code>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
